import { formatMoney } from './money';
import type { Item } from './types';

export interface BudgetState {
  budgetCents: number;
  /** Bar segments in percent of the budget. */
  boughtPct: number;
  mustPct: number;
  leftCents: number;
  message: string;
}

export interface Summary {
  openCount: number;
  openCents: number;
  mustCents: number;
  boughtCents: number;
  /** null when no budget is set. */
  budget: BudgetState | null;
}

const sum = (items: Pick<Item, 'price_cents'>[]) => items.reduce((s, i) => s + (i.price_cents || 0), 0);

/** Totals and budget text for the summary card, as in the Wunschschrank artifact. */
export function summarize(
  items: Pick<Item, 'price_cents' | 'priority' | 'bought'>[],
  budgetCents: number,
  currency?: string,
): Summary {
  const open = items.filter((i) => !i.bought);
  const bought = items.filter((i) => i.bought);
  const openCents = sum(open);
  const mustCents = sum(open.filter((i) => i.priority === 1));
  const boughtCents = sum(bought);

  let budget: BudgetState | null = null;
  if (budgetCents > 0) {
    const fmt = (c: number) => formatMoney(c, currency);
    const boughtPct = Math.min(100, (boughtCents / budgetCents) * 100);
    const mustPct = Math.max(0, Math.min(100 - boughtPct, (mustCents / budgetCents) * 100));
    const left = budgetCents - boughtCents;
    const afterMust = left - mustCents;
    let message: string;
    if (left < 0) message = `Budget um ${fmt(-left)} überzogen.`;
    else if (!mustCents) message = `Noch ${fmt(left)} von ${fmt(budgetCents)} übrig.`;
    else if (afterMust >= 0)
      message = `Noch ${fmt(left)} übrig. Alle Must-haves passen rein, danach bleiben ${fmt(afterMust)}.`;
    else message = `Noch ${fmt(left)} übrig. Für alle Must-haves fehlen ${fmt(-afterMust)}.`;
    budget = { budgetCents, boughtPct, mustPct, leftCents: left, message };
  }

  return { openCount: open.length, openCents, mustCents, boughtCents, budget };
}
